import React, { useState, useRef } from "react";
import { useNavigate } from "react-router";

import isEqual from "lodash/isEqual";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import AssetImage from "src/components/wizard/Item/AssetImage";
import ItemId from "src/views/shopping/Orders/ItemId";
import LocationSelectorModal from "./LocationSelectorModal/LocationSelectorModal";
import InventoryTransactionMultipleModal from "src/components/modals/InventoryTransactionMultipleModal/index";
import MobileLocationWarning from "./MobileLocationWarning";
import SelectMobileLocationModal from "src/views/capture/RackLocations/StorageLocationAutocomplete/SelectMobileLocationModal";
import { getAssetWithOverrideMobileLocation } from "./utils";
import MoveActions from "./MoveActions";
import MoveConfirmationModal from "./MoveConfirmationModal";
import { inventoryTransactionFormRef } from "src/components/modals/InventoryTransactionMultipleModal/index";
import useQueryParams from "src/hooks/useQueryParams";
import ConfirmMoveToSelectedLocationModal from "./ConfirmMoveToSelectedLocationModal";
import { formatRackLocationLabel } from "src/utils/rackLocations";

export const ItemInventoryMoveContext = React.createContext();

export default function ItemInventoryMove({ data, onClose }) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const actions = useRef({});
  const openInventoryModal = params.get("newInventoryLocations") === "true";
  const movedAssetIdsParam = params.get("movedAssetIds");
  const movedAssetIds = movedAssetIdsParam ? movedAssetIdsParam.split(",").filter(Boolean) : [];
  const newWarehouseLocationsParam = params.get("newWarehouseLocations");
  const isMoveToPreselectedLocationFlow = movedAssetIds.length > 0 && !!newWarehouseLocationsParam;
  const [openMobileLocationModal, setOpenMobileLocationModal] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [newLocationNames, setNewLocationNames] = useState([]);
  const [warehouseName, setWarehouseName] = useState("");
  const [mobileLocation, setMobileLocation] = useState(null);
  const [selectedRackLocation, setSelectedRackLocation] = useState(
    data?.assetsCollection?.collection?.[0]?.rackLocations?.length === 1 ? 0 : null
  );
  const [assetData] = data?.assetsCollection?.collection || [];
  const [overrideRackLocation, setOverrideRackLocation] = useState(null);
  const asset = getAssetWithOverrideMobileLocation(assetData, overrideRackLocation || {});
  const [autoSelect, setAutoSelect] = useState(null);
  const rackLocation = asset?.rackLocations?.[selectedRackLocation];
  const hasMobileLocation = rackLocation?.mobile_warehouse_location?.id;
  const newRackLocation = params.get("newRackLocation")
    ? params.get("newRackLocation")
    : null;

  const autoSelectLocation = newRackLocation || autoSelect
    ? { 
        ...rackLocation, 
        warehouse_locations: rackLocation?.warehouse_locations?.map((loc, index) => 
          index === 0 
            ? { ...loc, location_name: newRackLocation || autoSelect }
            : loc
        ) || [{ location_name: newRackLocation || autoSelect }]
      }
    : null;

  const setOpenInventoryModal = isOpen => {
    if (isOpen) {
      params.set("newInventoryLocations", "true");
    } else {
      params.delete("newInventoryLocations");
    }
    navigate({ search: params.toString() });
  };

  const handleLocationSelected = locationIndex => {
    setSelectedRackLocation(locationIndex);
  };

  const handleConsolidateMobileLocation = () => {
    setOpenMobileLocationModal(true);
  };

  const handleMobileLocationClose = () => {
    setOpenMobileLocationModal(false);
  };

  const handleMobileLocationOverride = mobileWarehouseLocation => {
    setOverrideRackLocation({
      mobile_warehouse_location: {
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.locationName,
      },
    });
    setOpenMobileLocationModal(false);
    setOpenInventoryModal(true);
  };

  const handleMobileLocationSubmit = (value, mobileWarehouseLocation) => {
    const [{ locationName } = {}] = value || [];
    setOverrideRackLocation({
      mobile_warehouse_location: {
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.locationName,
      },
    });
    setOpenMobileLocationModal(false);
    setOpenInventoryModal(true);
    setAutoSelect(locationName);
  };

  const handleCloseInventoryModal = () => {
    setOpenInventoryModal(false);
    setAutoSelect(null);
  };

  const handleInventoryMoveSuccess = responseData => {
    const assetData = responseData?.asset;
    const { rackLocations = [], warehousesFromRackLocations = [] } = assetData;
    // warehouse_locations is now an array - flatten all locations
    const names = rackLocations.flatMap(rl => 
      (rl.warehouse_locations || []).map(loc => loc.location_name)
    ).filter(Boolean);
    const firstWarehouse = warehousesFromRackLocations[0]?.name || "";
    const mobileLoc = rackLocations.find(rl => rl.mobile_warehouse_location)?.mobile_warehouse_location;

    setNewLocationNames(names);
    setWarehouseName(firstWarehouse);
    setMobileLocation(mobileLoc);
    if (actions.current.addMoreItems) {
      actions.current.addMoreItems = false;
    } else {
      setOpenInventoryModal(false);
      setConfirmationOpen(true);
    }
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationOpen(false);
    onClose();
  };

  const handleMoveToOtherWarehouse = ([location]) => {
    const newLocation = { warehouse: location.warehouse };
    if (location.mobile_warehouse_location?.id) {
      newLocation.mobile_warehouse_location = location.mobile_warehouse_location;
    }
    setOverrideRackLocation(newLocation);
    setOpenInventoryModal(true);
    setAutoSelect(location.locationName);
  };

  const handleMoveOnlyThisItem = () => {
    setOverrideRackLocation({
      mobile_warehouse_location: null,
    });
    setOpenInventoryModal(true);
  };

  const handleAddMoreItemsToMove = () => {
    const location = inventoryTransactionFormRef.current.values.locations[selectedRackLocation];
    const warehouseLocations = location.warehouse_locations || [];
    const mobileWarehouseLocation = location.mobile_warehouse_location;
    const warehouse = location.warehouse;

    // Need at least warehouse_locations to proceed
    if (warehouseLocations.length === 0) {
      return;
    }

    actions.current.addMoreItems = true;
    inventoryTransactionFormRef.current.submitForm();

    // Store the full warehouse_locations array as JSON so subsequent assets use the exact same location
    params.set("newWarehouseLocations", JSON.stringify(
      warehouseLocations.map(loc => ({
        id: loc.id,
        location_id: loc.location_id || loc.locationId,
        location_name: loc.location_name || loc.locationName,
      }))
    ));

    // Store the warehouse info so subsequent assets use the same warehouse
    if (warehouse?.id) {
      params.set("newWarehouse", JSON.stringify({
        id: warehouse.id,
        warehouse_id: warehouse.warehouse_id || warehouse.warehouseId,
        name: warehouse.name,
      }));
    }

    // Store the mobile location if present
    if (mobileWarehouseLocation?.id) {
      params.set("newMobileWarehouseLocation", JSON.stringify({
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.location_id || mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.location_name || mobileWarehouseLocation.locationName,
      }));
    }

    params.set("manualSearch", "true");

    const currentMovedIds = params.get("movedAssetIds");
    const movedIdsArray = currentMovedIds ? currentMovedIds.split(",") : [];
    if (asset?.id && !movedIdsArray.includes(asset.id)) {
      movedIdsArray.push(asset.id);
    }
    params.set("movedAssetIds", movedIdsArray.join(","));

    navigate({ search: params.toString() });
  };

  if (asset?.rackLocations?.length > 1 && typeof selectedRackLocation !== "number") {
    return (
      <LocationSelectorModal
        onClose={onClose}
        onSelectLocation={handleLocationSelected}
        asset={asset}
      />
    );
  }

  // When moving to a preselected location, show confirm modal
  // Use selectedRackLocation if set (multi-location asset), otherwise default to 0 (single-location asset)
  if (isMoveToPreselectedLocationFlow) {
    return (
      <ConfirmMoveToSelectedLocationModal
        asset={asset}
        sourceRackLocationIndex={typeof selectedRackLocation === "number" ? selectedRackLocation : 0}
      />
    );
  }

  if (openInventoryModal) {
    return (
      <ItemInventoryMoveContext.Provider value={{ selectedRackLocation, autoSelectLocation }}>
        <InventoryTransactionMultipleModal
          title="New Inventory Locations"
          displayPalletBreakdown={false}
          displayQuantityCalculation={false}
          displayNewAvailableQuantity={false}
          displayTotalQuantityChange={false}
          displayShipmentId={false}
          displayAddAnotherLocation={false}
          displayBulkActions={false}
          disableQuantity={true}
          displayDeleteLocation={false}
          displayDuplicate={false}
          open={true}
          onClose={handleCloseInventoryModal}
          onSuccess={handleInventoryMoveSuccess}
          asset={asset}
          openMoveItemAssistantModal={true}
          submitLabel="Confirm Locations"
          hideLocation={({ location, index, initialValues }) =>
            isEqual(location, initialValues.locations[index])
          }
          actionSource="inventory_move"
          secondaryAction={
            <Button variant="outlined" color="primary" onClick={handleAddMoreItemsToMove}>
              Add More Items to Move
            </Button>
          }
        />
      </ItemInventoryMoveContext.Provider>
    );
  }

  if (confirmationOpen) {
    return (
      <MoveConfirmationModal
        open={confirmationOpen}
        onClose={handleCloseConfirmationModal}
        locations={newLocationNames}
        warehouseName={warehouseName}
        mobileLocation={mobileLocation}
      />
    );
  }

  return (
    <>
      <Dialog open maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
          <Typography fontSize={24} fontWeight={500} color="textPrimary">
            Item Inventory Move
          </Typography>
          <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Image</TableCell>
                <TableCell align="center">Item ID</TableCell>
                {asset?.barcode && <TableCell align="center">Barcode</TableCell>}
                <TableCell align="center">Description</TableCell>
                <TableCell align="center">Location Quantity</TableCell>
                <TableCell align="center">Existing Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <AssetImage width={100} height={90} image={asset?.images?.[0]?.url} />
                </TableCell>
                <TableCell align="center">
                  <ItemId asset={asset} editable={true} showOnlySave={true} />
                </TableCell>
                {asset?.barcode && <TableCell align="center">{asset.barcode}</TableCell>}
                <TableCell align="center">{asset?.description}</TableCell>
                <TableCell align="center">{rackLocation?.sub_qty || 0}</TableCell>
                <TableCell align="center">
                  {asset?.rackLocations?.map((rl, index) => (
                    <Typography key={index}>{formatRackLocationLabel(rl)}</Typography>
                  ))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {hasMobileLocation && <MobileLocationWarning />}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <MoveActions
            hasMobileLocation={hasMobileLocation}
            onOpenInventoryModal={() => setOpenInventoryModal(true)}
            onConsolidateMobileLocation={handleConsolidateMobileLocation}
            onMoveToOtherWarehouse={handleMoveToOtherWarehouse}
            onMoveOnlyThisItem={handleMoveOnlyThisItem}
          />
        </DialogActions>
      </Dialog>
      {openMobileLocationModal && (
        <SelectMobileLocationModal
          onClose={handleMobileLocationClose}
          onOverride={handleMobileLocationOverride}
          onSubmit={handleMobileLocationSubmit}
        />
      )}
    </>
  );
}
